import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
  Select,
  Button,
  message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  getVendorInvoiceStatistics,
  exportVendorInvoiceStatistics,
} from "../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../core/api/settings";
import { useSelector } from "react-redux";
import { Row, Col } from "antd";
import ExcelExportModal from "../Shared/Modals/ExcelExportModal";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";

const { Text } = Typography;

const VendorInvoiceStatistics = ({ filters = {} }) => {
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState([]);
  const [error, setError] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [vendorNameFilter, setVendorNameFilter] = useState(null);

  const { selectedBrands, brandIds } = useGlobalFilters();

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);
    setStatistics([]); // clear old data

    try {
      const apiFilters = {};

      if (filters.dateRange?.length === 2) {
        apiFilters.start_date = filters.dateRange[0]?.format("YYYY-MM-DD");
        apiFilters.end_date = filters.dateRange[1]?.format("YYYY-MM-DD");
      }
      if (filters.partnerType && filters.partnerType !== "all") {
        apiFilters.partner_type = filters.partnerType;
      }
      if (filters.status && filters.status !== "all") {
        apiFilters.status = filters.status;
      }

      // Only show error if we're trying to fetch statistics but have no brands
      if (brandIds.length === 0) {
        setStatistics([]); // Clear statistics when no brand selected
        setLoading(false);
        return;
      }

      const data = await getVendorInvoiceStatistics(brandIds, apiFilters);

      const filteredData = (data || []).filter((vendor) => {
        const vendorBrandIds = Array.isArray(vendor.brand_ids)
          ? vendor.brand_ids
          : [vendor.brand_ids];
        return vendorBrandIds.some((id) => brandIds.includes(id));
      });

      const enrichedData = filteredData.map((vendor) => {
        const vendorBrandIds = Array.isArray(vendor.brand_ids)
          ? vendor.brand_ids
          : [vendor.brand_ids];
        const vendorBrandNames = vendorBrandIds
          .map((id) => selectedBrands.find((b) => b.id === id)?.brand_name)
          .filter(Boolean)
          .join(", ");
        return { ...vendor, brand_name: vendorBrandNames };
      });

      setStatistics(enrichedData);
    } catch (err) {
      console.error("Error fetching vendor statistics:", err);
      setError("Failed to load vendor statistics. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [filters, selectedBrands, brandIds]);

  // Fetch data when brands change
  useEffect(() => {
    if (brandIds && brandIds.length > 0) {
      fetchStatistics();
    }
    // eslint-disable-next-line
  }, [brandIds]);

  const handleExport = async (returnFile = false) => {
    // Remove the brand validation - allow export even with no brands selected
    setExportLoading(true);
    try {
      const apiFilters = {};

      if (filters.dateRange?.length === 2) {
        apiFilters.start_date = filters.dateRange[0]?.format("YYYY-MM-DD");
        apiFilters.end_date = filters.dateRange[1]?.format("YYYY-MM-DD");
      }

      if (filters.partnerType && filters.partnerType !== "all") {
        apiFilters.partner_type = filters.partnerType;
      }

      if (filters.status && filters.status !== "all") {
        apiFilters.status = filters.status;
      }

      // Use global brand IDs for export

      const response = await exportVendorInvoiceStatistics(
        brandIds,
        apiFilters
      );

      if (returnFile) {
        // Return blob for email functionality
        return new File([response.data], "vendor_invoice_statistics.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      } else {
        // Direct download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "vendor_invoice_statistics.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("Export completed successfully!");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data. Please try again.");
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const calculateTotals = (data = statistics) => {
    if (!data.length)
      return {
        invoices: 0,
        amount: 0,
        paid: 0,
        unpaid: 0,
        overpayment: 0,
        creditCount: 0,
        creditAmount: 0,
      };

    return data.reduce(
      (acc, vendor) => {
        acc.invoices += vendor.total_invoices;
        acc.amount += vendor.total_amount;
        acc.paid += vendor.total_paid;
        acc.unpaid += vendor.total_unpaid;
        acc.overpayment += vendor.overpayment_amount || 0;
        acc.creditCount += vendor.credit_count || 0;
        acc.creditAmount += vendor.credit_amount || 0;
        return acc;
      },
      {
        invoices: 0,
        amount: 0,
        paid: 0,
        unpaid: 0,
        overpayment: 0,
        creditCount: 0,
        creditAmount: 0,
      }
    );
  };

  const filteredStatistics = vendorNameFilter
    ? statistics.filter((v) => v.vendor_name === vendorNameFilter)
    : statistics;

  const totals = calculateTotals(filteredStatistics);

  const columns = [
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      sorter: (a, b) => a.vendor_name.localeCompare(b.vendor_name),
      width: 200,
    },
    {
      title: "Brand(s)",
      dataIndex: "brand_name",
      key: "brand_name",
      sorter: (a, b) => a.brand_name?.localeCompare(b.brand_name),
      render: (text) => <Text type="secondary">{text}</Text>,
      width: 150,
    },
    {
      title: "Total Invoices",
      dataIndex: "total_invoices",
      key: "total_invoices",
      sorter: (a, b) => a.total_invoices - b.total_invoices,
      render: (value) => value.toLocaleString(),
    },
    {
      title: "Total Credits",
      dataIndex: "credit_count",
      key: "credit_count",
      sorter: (a, b) => (a.credit_count || 0) - (b.credit_count || 0),
      render: (value) => (value || 0).toLocaleString(),
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      sorter: (a, b) => a.total_amount - b.total_amount,
      render: (value) =>
        `$${value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      title: "Total Paid",
      dataIndex: "total_paid",
      key: "total_paid",
      sorter: (a, b) => a.total_paid - b.total_paid,
      render: (value) =>
        `$${value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      title: "Total Unpaid",
      dataIndex: "total_unpaid",
      key: "total_unpaid",
      sorter: (a, b) => a.total_unpaid - b.total_unpaid,
      render: (value) => {
        const color = value > 0 ? "red" : "green";
        return (
          <Text style={{ color }}>
            $
            {value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        );
      },
    },
    {
      title: "Overpayment",
      dataIndex: "overpayment_amount",
      key: "overpayment_amount",
      sorter: (a, b) => a.overpayment_amount - b.overpayment_amount,
      render: (value) => {
        const color = value > 0 ? "blue" : "gray";
        return (
          <Text style={{ color }}>
            $
            {value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        );
      },
    },
    {
      title: "Credit Amount",
      dataIndex: "credit_amount",
      key: "credit_amount",
      sorter: (a, b) => (a.credit_amount || 0) - (b.credit_amount || 0),
      render: (value) =>
        `$${(value || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      title: "Payment Status",
      key: "payment_status",
      render: (_, record) => {
        const percentPaid =
          record.total_amount > 0
            ? (record.total_paid / record.total_amount) * 100
            : 0;
        if (percentPaid >= 99.99) return <Tag color="success">Fully Paid</Tag>;
        if (percentPaid > 0) return <Tag color="warning">Partially Paid</Tag>;
        return <Tag color="default">Unpaid</Tag>;
      },
    },
  ];

  return (
    <Card className="vendor-statistics-card">
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Vendor Filter with Export Button */}
        <Card type="inner" title="Filters">
          <Row gutter={24} align="middle">
            {/* Vendor Filter */}
            <Col flex="auto">
              <Space
                direction="horizontal"
                align="center"
                style={{ width: "100%" }}
              >
                <Text strong>Filter by Vendor:</Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Vendor"
                  style={{ width: 300 }}
                  optionFilterProp="children"
                  value={vendorNameFilter}
                  onChange={(value) => setVendorNameFilter(value)}
                  options={[
                    ...new Set(statistics.map((vendor) => vendor.vendor_name)),
                  ].map((name) => ({
                    label: name,
                    value: name,
                  }))}
                />
              </Space>
            </Col>

            {/* Export Button - removed disabled condition */}
            <Button
              onClick={() => setExportModalVisible(true)}
              loading={exportLoading}
            >
              {
                <ImageWithBasePath
                  src="assets/img/icons/xls.svg"
                  alt="Excel"
                  style={{ width: 16, height: 16 }}
                />
              }
            </Button>
          </Row>
        </Card>

        {/* Error message */}
        {error && <Alert message={error} type="error" showIcon />}

        {/* Summary */}
        {statistics.length > 0 && (
          <Card type="inner" title="Summary">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div>
                <Text strong>Total Vendors:</Text> {filteredStatistics.length}
              </div>
              <div>
                <Text strong>Total Invoices:</Text>{" "}
                {totals.invoices.toLocaleString()}
              </div>
              <div>
                <Text strong>Total Amount:</Text> $
                {totals.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div>
                <Text strong>Total Paid:</Text> $
                {totals.paid.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div>
                <Text strong>Total Unpaid:</Text>{" "}
                <Text style={{ color: totals.unpaid > 0 ? "red" : "green" }}>
                  $
                  {totals.unpaid.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </div>
              <div>
                <Text strong>Total Overpayment:</Text>{" "}
                <Text
                  style={{ color: totals.overpayment > 0 ? "blue" : "gray" }}
                >
                  $
                  {totals.overpayment.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </div>
              <div>
                <Text strong>Total Credits:</Text>{" "}
                {totals.creditCount.toLocaleString()}
              </div>
              <div>
                <Text strong>Credit Amount:</Text> $
                {totals.creditAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Table */}
        <Spin spinning={loading}>
          <Table
            dataSource={filteredStatistics}
            columns={columns}
            rowKey="vendor_id"
            pagination={{ pageSize: 10 }}
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell />
                  <Table.Summary.Cell>
                    <strong>{totals.invoices.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <strong>{totals.creditCount.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <strong>
                      $
                      {totals.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <strong>
                      $
                      {totals.paid.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <strong
                      style={{ color: totals.unpaid > 0 ? "red" : "green" }}
                    >
                      $
                      {totals.unpaid.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <strong
                      style={{
                        color: totals.overpayment > 0 ? "blue" : "gray",
                      }}
                    >
                      $
                      {totals.overpayment.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <strong>
                      $
                      {totals.creditAmount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Spin>

        {/* Export Modal */}
        <ExcelExportModal
          visible={exportModalVisible}
          onCancel={() => setExportModalVisible(false)}
          onExport={handleExport}
          exportType="Vendor Statistics"
          loading={exportLoading}
        />
      </Space>
    </Card>
  );
};

export default VendorInvoiceStatistics;
